import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";
import { BrazilDate } from "../../../../utils/BrazilDate";
import { useAuth } from "../../../../context/AuthContext";
import { producaoService } from "../../../../services/producaoService";
import { lojaService } from "../../../../services/lojaService";
import { Loja } from "../../../../types/loja";
import { UserStats, DailyStats } from "../types";
import { parseHHMMSSToSeconds } from "../utils/formatTime";
import { useRankingEvents } from "../../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../../constants/rankingEventQueryKeys";

interface RankingProducaoContextData {
  pizzaiolos: UserStats[];
  dailyStats: DailyStats;
  lojas: Loja[];
  isLoading: boolean;
  selectedLojaId?: number;
  showLojaSelector: boolean;
  dateLabel: string;
  selectedLojaNome?: string;
  toggleLojaSelector: () => void;
  selectAllLojas: () => void;
  selectLoja: (id: number) => void;
}

interface RankingProducaoProviderProps {
  children: React.ReactNode;
}

const RankingProducaoContext = createContext<RankingProducaoContextData>(
  {} as RankingProducaoContextData
);

export const RankingProducaoProvider: React.FC<
  RankingProducaoProviderProps
> = ({ children }) => {
  const { user } = useAuth();
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );
  const [showLojaSelector, setShowLojaSelector] = useState(false);

  const getTodayDate = useCallback(() => {
    const now = BrazilDate.now();
    const timezone = BrazilDate.getTimezone();
    return format(now, "dd/MM/yyyy", { timeZone: timezone });
  }, []);

  const dateLabel = getTodayDate();

  const { data: lojas = [], isLoading: isLoadingLojas } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const rankingDiaFilters = useMemo(
    () => ({ lojaId: selectedLojaId ?? null }),
    [selectedLojaId]
  );

  const { data: rankingDiaData, isLoading: isLoadingRankingDia } = useQuery({
    queryKey: ["ranking-dia-producao", rankingDiaFilters],
    queryFn: () => producaoService.getRankingDia(rankingDiaFilters),
    refetchInterval: false,
  });

  useRankingEvents("producao", selectedLojaId, {
    queryKeys: RANKING_SSE_QUERY_KEYS.rankingProducao,
  });

  const isLoading = isLoadingLojas || isLoadingRankingDia;

  const pizzaiolos: UserStats[] = useMemo(() => {
    if (!rankingDiaData?.ranking) return [];

    return rankingDiaData.ranking.map((item) => ({
      nome: item.nome.split(" ")[0] || "",
      sobrenome: item.nome.split(" ").slice(1).join(" ") || "",
      cargo: "pizzaiolo",
      producao: item.quantidadePizzas,
      tempoMedio: parseHHMMSSToSeconds(item.tempoMedio),
    }));
  }, [rankingDiaData]);

  const dailyStats: DailyStats = useMemo(() => {
    if (!rankingDiaData) {
      return {
        pizzasFinalizadas: 0,
        tempoMedioGeral: 0,
        pizzaiolos: 0,
        emProducao: 0,
      };
    }

    return {
      pizzasFinalizadas: rankingDiaData.quantidadeTotal,
      tempoMedioGeral: parseHHMMSSToSeconds(rankingDiaData.tempoMedioGeral),
      pizzaiolos: rankingDiaData.ranking.length,
      emProducao: rankingDiaData.quantidadeEmProducao,
    };
  }, [rankingDiaData]);

  const selectedLojaNome = useMemo(
    () => lojas.find((l) => l.id === selectedLojaId)?.nome,
    [lojas, selectedLojaId]
  );

  const toggleLojaSelector = useCallback(() => {
    setShowLojaSelector((prev) => !prev);
  }, []);

  const selectAllLojas = useCallback(() => {
    setSelectedLojaId(undefined);
    setShowLojaSelector(false);
  }, []);

  const selectLoja = useCallback((id: number) => {
    setSelectedLojaId(id);
    setShowLojaSelector(false);
  }, []);

  return (
    <RankingProducaoContext.Provider
      value={{
        pizzaiolos,
        dailyStats,
        lojas,
        isLoading,
        selectedLojaId,
        showLojaSelector,
        dateLabel,
        selectedLojaNome,
        toggleLojaSelector,
        selectAllLojas,
        selectLoja,
      }}
    >
      {children}
    </RankingProducaoContext.Provider>
  );
};

export const useRankingProducao = (): RankingProducaoContextData => {
  const context = useContext(RankingProducaoContext);
  if (!context) {
    throw new Error(
      "useRankingProducao must be used within RankingProducaoProvider"
    );
  }
  return context;
};
